'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { AlertCircle, Loader2 } from 'lucide-react';
import { formatLegalTextSimple } from '@/components/ui/LegalTextFormatter';
import { cn } from '@/lib/utils';

export interface ReferencedArticle {
  articleNumber: string;
  articleContent: string;
  sourceRegulation?: string;
  previousSourceRegulation?: string;
  history?: Array<{
    regulationTitle: string;
    kind: string;
  }>;
}

interface ArticleReferenceTooltipProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  hasError: boolean;
  notFound: boolean;
  article: ReferencedArticle | null;
  articleNumber: string;
}

/**
 * Renders the content inside the article reference tooltip.
 * Handles various states: unauthenticated, loading, error, not found, and success.
 */
export function ArticleReferenceTooltip({
  isAuthenticated,
  isLoading,
  hasError,
  notFound,
  article,
  articleNumber,
}: ArticleReferenceTooltipProps) {
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col gap-3 text-sm">
        <div className="text-gray-600">
          Masuk untuk lihat pasal yang direferensikan tanpa harus scroll-scroll ke atas
        </div>
        <a
          href="/login"
          className="inline-flex items-center justify-center px-4 py-2 bg-black text-white rounded-none hover:bg-gray-800 transition-colors text-sm font-medium"
        >
          Masuk
        </a>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 size={16} className="animate-spin" />
        <span>Memuat pasal...</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <AlertCircle size={16} />
        <span>Gagal memuat pasal. Coba lagi nanti.</span>
      </div>
    );
  }

  if (notFound) {
    return <div className="text-sm text-gray-600">Pasal tidak ditemukan</div>;
  }

  if (article) {
    return (
      <div className="article-tooltip-scroll max-h-[280px] overflow-y-auto pr-1 -mr-1">
        <div className="font-bold text-sm mb-2 pb-2 border-b border-gray-200 pr-2">
          Pasal {article.articleNumber}
        </div>
        <div className="text-xs leading-relaxed text-justify break-words pr-2">
          {formatLegalTextSimple(article.articleContent)}
        </div>
        {article.sourceRegulation && (
          <div className="mt-3 pt-2 border-t border-gray-100 text-[11px] text-gray-500">
            <div>
              {article.previousSourceRegulation ? 'Diubah oleh:' : 'Sumber:'}{' '}
              {article.sourceRegulation}
            </div>
            {article.previousSourceRegulation && (
              <div>(Sebelumnya: {article.previousSourceRegulation})</div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}

interface ArticleReferenceTooltipWrapperProps extends ArticleReferenceTooltipProps {
  children: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  articleNumber: string;
}

/**
 * Wrapper component that provides the Radix UI Tooltip structure.
 * Renders the trigger and the content with proper styling.
 */
export function ArticleReferenceTooltipWrapper({
  children,
  isOpen,
  onOpenChange,
  articleNumber,
  ...tooltipProps
}: ArticleReferenceTooltipWrapperProps) {
  return (
    <TooltipPrimitive.Root open={isOpen} onOpenChange={onOpenChange} delayDuration={0}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>

      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="top"
          align="start"
          sideOffset={8}
          className={cn(
            'z-50 overflow-hidden rounded-md border-2 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            'w-[380px] sm:w-[480px] max-h-[300px]'
          )}
        >
          <div className="p-3">
            <ArticleReferenceTooltip articleNumber={articleNumber} {...tooltipProps} />
          </div>
          <TooltipPrimitive.Arrow className="fill-black" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
